import React, { useEffect } from 'react'

import { SCALE_TYPES, REGIONS, REGION_LEVELS } from 'nswidgets/src/components/map/constants'
import { Select } from '../../../common/components/select'
import { ExplicitChangeTextInput } from '../../../common/components/explicit-change-text-input'
import { Switch, SWITCH_TYPE } from '../../../common/components/switch'
import { usePageEditorContext } from '../../contexts/page-editor.context'
import { Accordion, AccordionSummary, AccordionDetails } from '../../editor/sidebar-accordion'
import { ManualBlockEditor } from '../../editor/manual-block-editor'
import { formatOptionsService } from '../format-options.service'
import { reportDataSetService } from '../../editor/report-data-set.service'
import { HeaderEditAccordion } from '../header-edit-accordion'
import { FooterEditAccordion } from '../footer-edit-accordion'
import { FilterEditAccordion } from '../filter-edit-accordion'
import { SplitChartFilterEditAccordion } from '../split-chart-filter-edit-accordion'

const SCALE_TYPE_OPTIONS = [
  { value: SCALE_TYPES.SYMLOG, label: 'Symlog' },
  { value: SCALE_TYPES.QUANTIZE, label: 'Quantize' },
  { value: SCALE_TYPES.DIVERGING_SYMLOG, label: 'Diverging Symlog' },
  { value: SCALE_TYPES.DIVERGING, label: 'Diverging' }
]

const COLOR_RANGE_OPTIONS = [
  { value: 'red', label: 'Red' },
  { value: 'blue', label: 'Blue' }
]

const SYMLOG_CONSTANT_OPTIONS = [
  { value: 1, label: '1' },
  { value: 10, label: '10' },
  { value: 100, label: '100' },
  { value: 1000, label: '1,000' },
  { value: 10000, label: '10,000' },
  { value: 100000, label: '100,000' },
  { value: 0.1, label: '0.1' },
  { value: 0.01, label: '0.01' },
  { value: 0.001, label: '0.001' },
  { value: 0.0001, label: '0.0001' },
  { value: 0.00001, label: '0.00001' }
]

const DEFAULT_SYMLOG_CONSTANT = 1
const DEFAULT_DIVERGING_FROM = 0

export const MapBlockEditSidebar = function ({ block }) {
  const pageEditorContext = usePageEditorContext() || {}

  const filterOptionLabels = reportDataSetService.getFilterOptionLabels(block)

  useEffect(() => {
    // When other filters are selected, reference filter should be disabled.
    const otherFiltersExist = hasOtherFilters()
    const isZipLevel = getBlockProp('regionLevel') === REGION_LEVELS.ZIP
    if (otherFiltersExist || !isZipLevel) {
      toggleReferenceFilter(false)
    }
  }, [hasOtherFilters(), getBlockProp('regionLevel')])

  function updateBlock (block) {
    pageEditorContext.registerUnsavedChanges()
    pageEditorContext.updateCurrentEdit({ block: { ...block } })
  }

  function getBlockProp (prop, defaultValue) {
    if (block.props[prop] === undefined) {
      return defaultValue
    } else {
      return block.props[prop]
    }
  }

  function setBlockProp (prop, value) {
    block.props[prop] = value
    updateBlock(block)
  }

  function getFormatValue (prop) {
    let value = block.props[prop]
    return formatOptionsService.getSelectedFormatOption(prop, value)
  }

  function setFormatValue (prop, value) {
    block.props[prop] = value
    updateBlock(block)
  }

  function handleScaleOptionsChange (key, value) {
    const scaleOptions = getBlockProp('scaleOptions')
    const type = (key === 'type') ? value : scaleOptions.type
    const constant = (key === 'constant') ? Number(value) : scaleOptions.constant || DEFAULT_SYMLOG_CONSTANT
    const divergingFrom = (key === 'divergingFrom') ? Number(value) : scaleOptions.divergingFrom === undefined ? DEFAULT_DIVERGING_FROM : scaleOptions.divergingFrom

    switch (type) {
      case SCALE_TYPES.SYMLOG:
        setBlockProp('scaleOptions', { type, constant })
        break
      case SCALE_TYPES.QUANTIZE:
        setBlockProp('scaleOptions', { type })
        break
      case SCALE_TYPES.DIVERGING_SYMLOG:
        setBlockProp('scaleOptions', { type, constant, divergingFrom })
        break
      case SCALE_TYPES.DIVERGING:
        setBlockProp('scaleOptions', { type, divergingFrom })
        break
    }
  }

  function isReferenceFilterOn () {
    return !!(block.props.filterDefinition && block.props.filterDefinition.thresholdFilters)
  }

  function toggleReferenceFilter (enable) {
    if (enable && !isReferenceFilterOn()) {
      block.props.filterDefinition.thresholdFilters = {
        buttonLabel: 'Filter Zip Codes',
        toggledButtonLabel: 'Clear Zip Code Filter',
        fields: [
          {
            key: 'race',
            sliderLabel: 'Percent of Population',
            label: 'Racial Composition',
            dataSource: {
              subgroup: 'race',
              regionLevel: 'zip',
              region: 'nc'
            },
            options: [
              {
                value: 'white',
                label: 'White'
              },
              {
                value: 'black',
                label: 'Black'
              },
              {
                value: 'asian',
                label: 'Asian'
              },
              {
                value: 'other',
                label: 'Other'
              }
            ]
          }
        ]
      }
      updateBlock(block)
    } else if (!enable && isReferenceFilterOn()) {
      delete block.props.filterDefinition.thresholdFilters
      updateBlock(block)
    }
  }

  function hasOtherFilters () {
    if (!block.props.filterDefinition || !block.props.filterDefinition.fields || (block.props.filterDefinition.fields.length === 0)) {
      return false
    }

    const otherFilters = block.props.filterDefinition.fields.filter(({ isPrimary }) => (!isPrimary))
    return otherFilters.length > 0
  }

  function getSymlogConstantOptions (shouldAddCustom) {
    if (shouldAddCustom) {
      return [
        ...SYMLOG_CONSTANT_OPTIONS,
        { value: 'custom', label: 'Custom' }
      ]
    } else {
      return SYMLOG_CONSTANT_OPTIONS
    }
  }

  function getOptionsForSymlogConstant () {
    const scaleOptions = getBlockProp('scaleOptions') || {}
    const symlogConstantOptions = getSymlogConstantOptions()
    const selectedOption = symlogConstantOptions.find(({ value }) => (value === scaleOptions.constant))

    return getSymlogConstantOptions(!selectedOption && scaleOptions.constant)
  }

  function getSelectedSymlogConstantOption () {
    const scaleOptions = getBlockProp('scaleOptions') || {}
    const symlogConstantOptions = getOptionsForSymlogConstant()
    const selectedOption = symlogConstantOptions.find(({ value }) => (value === scaleOptions.constant))

    if (!selectedOption && scaleOptions.constant) {
      return symlogConstantOptions.find(({ value }) => (value === 'custom'))
    } else {
      return selectedOption
    }
  }

  function buildFormatField (className, prop, label) {
    let isDisabled = false

    const dataDefinition = getBlockProp('dataDefinition', {})
    if (
      (prop === 'valueType' && !dataDefinition.valueField) ||
      (prop === 'value2Type' && !dataDefinition.value2Field)
    ) {
      isDisabled = true
    }

    return (
      <div className={className} data-qa={`field-${label}`}>
        <Select
          label={label}
          options={formatOptionsService.getLineBarMapFormatOptions(prop)}
          size="medium"
          value={getFormatValue(prop)}
          isDisabled={isDisabled}
          onChange={({ value }) => {
            setFormatValue(prop, value)
          }}
        />
      </div>
    )
  }

  const scaleOptions = getBlockProp('scaleOptions') || {}

  return <div className="report-editor-sidebar">
    {block.dataKey && <>
      <Accordion square defaultExpanded={true}>
        <AccordionSummary>
          Labels
        </AccordionSummary>
        <AccordionDetails>
          <div className="field-grid">
            <div className="full-width-field">
              <ExplicitChangeTextInput
                size="medium"
                label="Value Label"
                value={getBlockProp('valueLabel')}
                onChange={(event) => {
                  setBlockProp('valueLabel', event.target.value)
                }}
              />
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
      <Accordion square>
        <div data-qa="accordion-summary-format">
          <AccordionSummary>
            Format
          </AccordionSummary>
        </div>
        <AccordionDetails>
          <div className="field-grid">
            {buildFormatField('full-width-field', 'valueType', 'Value Format')}
            {buildFormatField('full-width-field', 'value2Type', 'Secondary Value Format')}
          </div>
        </AccordionDetails>
      </Accordion>
      <Accordion square>
        <div data-qa="accordion-summary-color-scale">
          <AccordionSummary>
            Color Scale
          </AccordionSummary>
        </div>
        <AccordionDetails>
          <div className="field-grid" data-qa="accordion-color-scale">
            <div className="full-width-field" data-qa="field-type">
              <Select
                label="Type"
                options={SCALE_TYPE_OPTIONS}
                size="medium"
                value={SCALE_TYPE_OPTIONS.find((scale) => (scale.value === scaleOptions.type))}
                onChange={({ value }) => {
                  handleScaleOptionsChange('type', value)
                }}
              />
            </div>
            <div className="field" data-qa="field-symlog-constant">
              <Select
                label="Symlog Constant"
                options={getOptionsForSymlogConstant()}
                size="medium"
                value={getSelectedSymlogConstantOption()}
                isDisabled={(scaleOptions.type === SCALE_TYPES.QUANTIZE) || (scaleOptions.type === SCALE_TYPES.DIVERGING)}
                onChange={({ value }) => {
                  if (value !== 'custom') {
                    handleScaleOptionsChange('constant', value)
                  }
                }}
              />
            </div>
            <div className="field">
              <ExplicitChangeTextInput
                disabled={(scaleOptions.type === SCALE_TYPES.QUANTIZE) || (scaleOptions.type === SCALE_TYPES.SYMLOG)}
                size="medium"
                label="Diverging From"
                value={scaleOptions.divergingFrom}
                onChange={(event) => {
                  handleScaleOptionsChange('divergingFrom', event.target.value)
                }}
              />
            </div>
            <div className="field" data-qa="field-color-range">
              <Select
                label="Color Range"
                options={COLOR_RANGE_OPTIONS}
                size="medium"
                value={COLOR_RANGE_OPTIONS.find((scale) => (scale.value === getBlockProp('colorRange')))}
                isDisabled={!scaleOptions.type || (scaleOptions.type === SCALE_TYPES.DIVERGING) || (scaleOptions.type === SCALE_TYPES.DIVERGING_SYMLOG)}
                onChange={({ value }) => {
                  setBlockProp('colorRange', value)
                }}
              />
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
    </>}
    {filterOptionLabels.filter(({ values, isSplitChartFilter }) => !isSplitChartFilter && values.length > 0).length > 0 && (
      <FilterEditAccordion block={block} />
    )}
    <SplitChartFilterEditAccordion block={block} />
    {block.dataKey && <>
      <HeaderEditAccordion block={block} />
      <FooterEditAccordion block={block} />
    </>}
    <Accordion square>
      <AccordionSummary>
        Other
      </AccordionSummary>
      <AccordionDetails>
        <div className="field-grid">
          {(getBlockProp('region') === REGIONS.NC) && (getBlockProp('regionLevel') === REGION_LEVELS.ZIP) && !hasOtherFilters() && (
            <div className="full-width-field">
              <div className="form-element-label-medium">Reference Filter</div>
              <Switch
                type={SWITCH_TYPE.MINI}
                isOn={isReferenceFilterOn()}
                onToggle={(event) => {
                  toggleReferenceFilter(event.target.checked)
                }}
              />
            </div>
          )}
          <div className="full-width-field">
            <ManualBlockEditor block={block} onChange={updateBlock} />
          </div>
        </div>
      </AccordionDetails>
    </Accordion>
  </div>
}
